import { http, apiConfig } from "@api/base";
import { serializer } from "./serializer";
import {
  validateCoverImage,
  convertFileToBase64,
  FILE_SIZE_LIMITS,
  COMMON_IMAGE_TYPES,
} from "@utils/fileUtils";

const FILES_URL = "/files/";
const DOCUMENTS_URL = "/documents/";
const PLACEHOLDER_URL = `${process.env.REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL}/static/images/placeholder.png`;

const JSON_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const PATCH_HEADERS = {
  "Content-Type": "application/json-patch+json",
  Accept: "application/json",
};

const downloadURL = (bucket, filename) =>
  `${apiConfig.baseURL}${FILES_URL}${bucket}/${filename}?download`;

const del = async (bucket, filename) => {
  const response = await http.delete(`${FILES_URL}${bucket}/${filename}`);
  response.data = serializer.fromJSON(response.data);
  return response;
};

const upload = async (bucket, file) => {
  const headers = {
    headers: {
      "Content-Type": (file.type || "").indexOf("/") > -1 ? file.type : "",
    },
  };
  const response = await http.put(
    `${FILES_URL}${bucket}/${file.name}`,
    file,
    headers
  );
  response.data = serializer.fromJSON(response.data);
  response.data.bucket = bucket;
  return response;
};

const validateCoverFile = (file) => {
  if (!file) throw new Error("File is required for cover upload");

  // Use centralized validation
  const validation = validateCoverImage(file);
  if (!validation.valid) {
    throw new Error(validation.errors.join("; "));
  }

  return validation;
};

const createCoverMetadata = (file, base64Data) => ({
  type: "manual_upload",
  filename: file.name,
  file_size: file.size,
  file_type: file.type,
  uploaded_at: new Date().toISOString(),
  urls: {
    is_placeholder: false,
    large: base64Data,
    medium: base64Data,
    small: base64Data,
  },
});

const createPatchData = (path, value) => [{ op: "replace", path, value }];

const uploadCover = async (documentPid, file) => {
  if (!documentPid)
    throw new Error("Document PID is required for cover upload");

  validateCoverFile(file);

  const base64Data = await convertFileToBase64(file);
  const coverMetadata = createCoverMetadata(file, base64Data);

  const currentDoc = await http.get(`${DOCUMENTS_URL}${documentPid}`);
  const updateData = {
    ...currentDoc.data.metadata,
    cover_metadata: coverMetadata,
  };

  await http.put(`${DOCUMENTS_URL}${documentPid}`, updateData, {
    headers: JSON_HEADERS,
  });

  return {
    data: {
      cover_metadata: coverMetadata,
      cover_urls: coverMetadata.urls,
    },
  };
};

const deleteCover = async (documentPid) => {
  if (!documentPid)
    throw new Error("Document PID is required for cover deletion");

  const placeholderMetadata = {
    urls: {
      is_placeholder: true,
      large: PLACEHOLDER_URL,
      medium: PLACEHOLDER_URL,
      small: PLACEHOLDER_URL,
    },
  };

  const patchData = createPatchData("/cover_metadata", placeholderMetadata);
  await http.patch(`${DOCUMENTS_URL}${documentPid}`, patchData, {
    headers: PATCH_HEADERS,
  });
  return { success: true };
};

const createDocumentBucket = async (documentPid) => {
  const response = await http.post(`${DOCUMENTS_URL}${documentPid}/bucket`);
  response.data = serializer.fromJSON(response.data);
  return response;
};

const getCoverURL = (document, size = "medium") => {
  const coverUrls = document?.metadata?.cover_metadata?.urls;
  if (!coverUrls || coverUrls.is_placeholder) return null;
  return (
    coverUrls[size] || coverUrls.medium || coverUrls.large || coverUrls.small
  );
};

const hasManualCover = (document) =>
  document?.metadata?.cover_metadata?.type === "manual_upload";

const hasISBNCover = (document) => {
  const coverMetadata = document?.metadata?.cover_metadata;
  return coverMetadata?.ISBN && coverMetadata.type !== "manual_upload";
};

// Export file validation constants and functions for use in components
export const fileValidation = {
  MAX_FILE_SIZE: FILE_SIZE_LIMITS.MAX_5MB,
  ALLOWED_IMAGE_TYPES: COMMON_IMAGE_TYPES,
  validateCoverFile,
};

export const fileApi = {
  filesBaseURL: FILES_URL,
  downloadURL,
  delete: del,
  upload,
  cover: {
    upload: uploadCover,
    delete: deleteCover,
    getCoverURL,
    hasManualCover,
    hasISBNCover,
  },
  document: {
    createBucket: createDocumentBucket,
  },
  validation: fileValidation,
};
